<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

interface Tab {
    value: string;
    title?: string;
    icon?: string | null;
    localizetitle?: boolean;
}

interface Props {
    tabs: Tab[];
    selected: string | null;
}

interface Indicator {
    left: number;
    width: number;
    visible: boolean;
}

const props = defineProps<Props>()

const emit = defineEmits<{
    select: [value: string];
}>()

// How far the indicators are pulled in from the edges of their tab
const INK_INSET = 10
const GHOST_INSET = 14

const list = ref<HTMLElement | null>(null)
const buttons: HTMLElement[] = []
const ink = ref<Indicator>({ left: 0, width: 0, visible: false })
const ghost = ref<Indicator>({ left: 0, width: 0, visible: false })
const animate = ref(false)

let resizeObserver: ResizeObserver | null = null

const setButton = (el: unknown, index: number): void => {
    if (el instanceof HTMLElement) {
        buttons[index] = el
    }
}

const measure = (value: string | null, inset: number): { left: number; width: number } | null => {
    const button = buttons[props.tabs.findIndex((tab) => tab.value === value)]

    // A tablist inside a hidden container has no layout yet
    if (! button || button.offsetWidth === 0) {
        return null
    }

    return { left: button.offsetLeft + inset, width: button.offsetWidth - inset * 2 }
}

const afterNextPaint = (callback: () => void): void => {
    requestAnimationFrame(() => requestAnimationFrame(callback))
}

const placeInk = (): void => {
    const position = measure(props.selected, INK_INSET)

    if (! position) {
        ink.value = { ...ink.value, visible: false }
        return
    }

    // Snap into place the first time the tablist becomes visible, glide on every later change
    if (! ink.value.visible) {
        animate.value = false
        afterNextPaint(() => { animate.value = true })
    }

    ink.value = { ...position, visible: true }

    if (! ghost.value.visible) {
        ghost.value = { ...ghost.value, left: position.left, width: position.width }
    }
}

const showGhost = (tab: Tab): void => {
    const position = tab.value === props.selected ? null : measure(tab.value, GHOST_INSET)

    ghost.value = position
        ? { ...position, visible: true }
        : { ...ghost.value, visible: false }
}

const hideGhost = (): void => {
    ghost.value = { ...ghost.value, visible: false }
}

const observe = (): void => {
    resizeObserver?.disconnect()
    if (list.value) {
        resizeObserver?.observe(list.value)
    }
    buttons.forEach((button) => resizeObserver?.observe(button))
}

watch(() => props.selected, () => {
    hideGhost()
    nextTick(placeInk)
})

watch(() => props.tabs.length, () => {
    buttons.length = props.tabs.length
    nextTick(() => {
        observe()
        placeInk()
    })
})

onMounted(() => {
    resizeObserver = new ResizeObserver(() => placeInk())
    observe()
    placeInk()
})

onBeforeUnmount(() => {
    resizeObserver?.disconnect()
})
</script>

<template>
    <div
        ref="list"
        class="relative flex items-end gap-0.5 px-1.5"
        role="tablist"
        @mouseleave="hideGhost"
    >
        <button
            v-for="(tab, index) in tabs"
            :key="tab.value"
            :ref="(el) => setButton(el, index)"
            type="button"
            role="tab"
            :aria-selected="tab.value === selected"
            :title="tab.title"
            class="relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3 pt-2.5 pb-3 text-sm font-medium transition duration-200 focus:outline-none focus-visible:rounded-t-md focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-wlPrimary tracking-tight"
            :class="tab.value === selected
                ? 'text-gray-900'
                : 'text-gray-700 hover:-translate-y-px hover:text-gray-900'"
            @mouseenter="showGhost(tab)"
            @click.prevent="emit('select', tab.value)"
        >
            <!-- The wrapper carries the accent so icons that bring their own colour class keep it -->
            <span
                v-if="tab.icon"
                class="transition-colors duration-200"
                :class="{ 'text-wlPrimary': tab.value === selected }"
            ><i :class="tab.icon"></i></span>
            <span v-html="tab.localizetitle ? _mft(tab.title) : tab.title"></span>
        </button>

        <span
            aria-hidden="true"
            class="tab-ghost"
            :class="{ 'is-visible': ghost.visible }"
            :style="{ left: `${ghost.left}px`, width: `${ghost.width}px` }"
        ></span>
        <span
            v-show="ink.visible"
            aria-hidden="true"
            class="tab-ink"
            :class="{ 'is-animated': animate }"
            :style="{ left: `${ink.left}px`, width: `${ink.width}px` }"
        ></span>
    </div>
</template>

<style scoped>
.tab-ink,
.tab-ghost {
    position: absolute;
    bottom: 0;
    pointer-events: none;
}

.tab-ink {
    height: 3px;
    border-radius: 3px 3px 0 0;
    background: var(--whitelabel-primary, #7814D7);
}

.tab-ghost {
    height: 3px;
    border-radius: 3px 3px 0 0;
    background: #d1d5db;
    opacity: 0;
    transition: opacity 0.2s, left 0.3s ease, width 0.3s ease;
}

.tab-ghost.is-visible {
    opacity: 1;
}

.is-animated {
    transition: left 0.5s cubic-bezier(0.34, 1.3, 0.64, 1), width 0.5s cubic-bezier(0.34, 1.3, 0.64, 1);
}

@media (prefers-reduced-motion: reduce) {
    .is-animated,
    .tab-ghost {
        transition: none;
    }
}
</style>
